/**
 * Sentry error monitoring — frontend.
 *
 * No-op when REACT_APP_SENTRY_DSN is unset (preview/dev/CI runs silent).
 * When set, captures unhandled exceptions and 5xx-class network failures.
 *
 * HIPAA safeguard: a `beforeSend` hook removes anything that *looks* like
 * PHI (numeric IDs that could be patient/clinician UUIDs, query-string
 * values, request bodies). Bearer tokens / cookies are also stripped.
 */
import * as Sentry from "@sentry/react";

const PHI_KEY_BLOCKLIST = new Set([
  "authorization", "cookie", "x-api-key",
  "first_name", "last_name", "email", "phone",
  "ssn", "tax_id", "dob", "date_of_birth", "address",
  "patient_name", "patient_initials",
]);

function _redact(obj) {
  if (!obj || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map(_redact);
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (PHI_KEY_BLOCKLIST.has(k.toLowerCase())) {
      out[k] = "[REDACTED]";
    } else if (v && typeof v === "object") {
      out[k] = _redact(v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return false;
  try {
    Sentry.init({
      dsn,
      environment: process.env.REACT_APP_SENTRY_ENV || "production",
      release: process.env.REACT_APP_SENTRY_RELEASE,
      // Tracing is optional + off by default — flip via env when needed.
      tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || "0"),
      // No session replay by default (replay can capture form input ~= PHI).
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      sendDefaultPii: false,
      beforeSend(event) {
        try {
          if (event.request) {
            // Strip headers + cookies + URL query (PHI lives there).
            event.request.cookies = undefined;
            event.request.headers = undefined;
            if (event.request.query_string) {
              event.request.query_string = "[REDACTED]";
            }
            if (event.request.data) {
              event.request.data = _redact(event.request.data);
            }
          }
          if (event.extra) event.extra = _redact(event.extra);
          if (event.contexts?.state) {
            event.contexts.state = "[REDACTED]";
          }
        } catch (_e) { /* never block error reporting */ }
        return event;
      },
    });
    return true;
  } catch (e) {
    // Sentry init failure must never break the app.
    // eslint-disable-next-line no-console
    console.warn("[sentry] init failed, continuing without monitoring", e);
    return false;
  }
}

export { Sentry };
